"use client";
import {
  ButtonComponent,
  DividerComponent,
  LoaderComponent,
  PaperComponent,
} from "@/components";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Circle } from "@mui/icons-material";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import {
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useTranslations } from "next-intl";
import {
  useGetApplicationQuery,
  useGetCustomerQuery,
  useInitiateRpdMutation,
  useRpdStatusQuery,
} from "@/lib/redux";
import useErrorHandling from "@/lib/hooks/errorhandling";
import useLogoutHook from "@/lib/hooks/logoutHook";
import { useRouter } from "next/navigation";
import { useFooter } from "@/lib/context/FooterContext";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import ROUTES from "@/lib/constants/PageRoutes";

const POLLING_INTERVAL = 10000;

const ReversePennyDrop = ({ product }) => {
  const [progress, setProgress] = useState(0);
  const { setActions } = useFooter();
  const [polling, setPolling] = useState(POLLING_INTERVAL);
  const t = useTranslations("ReversePennyDrop");
  const router = useRouter();
  const { handleLogout } = useLogoutHook();
  const [
    reversePennyDrop,
    {
      data: reversePennyDropData,
      error: errorReversePennyDrop,
      isLoading: reversePennyDropLoading,
      isSuccess: rpdSuccess,
    },
  ] = useInitiateRpdMutation();
  const {
    data: applicationData,
    isLoading: applicationLoading,
    error: errorGetApplication,
    refetch: refetchApplication,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });
  const { data: customerData } = useGetCustomerQuery();
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data
  );

  const { data: rpdStatusData } = useRpdStatusQuery(
    { appId: productApplication?.appId },
    {
      skip: !productApplication?.appId && !rpdSuccess,
      pollingInterval: polling === 0 ? undefined : polling,
    }
  );

  // Refetch application query every time rpdStatusQuery is polled
  useEffect(() => {
    if (polling !== 0) {
      const interval = setInterval(() => {
        refetchApplication();
      }, polling);
      return () => clearInterval(interval);
    }
  }, [polling, refetchApplication]);

  useEffect(() => {
    if (
      productApplication?.appId &&
      productApplication?.stage === STAGE?.RPD_MODE
    ) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.RPD_INITIATED,
        ...productApplication,
        ...customerData?.data,
      });
      reversePennyDrop({ appId: productApplication?.appId });
    }
  }, [productApplication?.stage]);

  useEffect(() => {
    setActions({
      showReject: true,
      showConfirm: false,
    });
  }, [setActions]);

  useEffect(() => {
    if (polling === 0) return;
    const maxPollingTime = 15 * 60 * 1000; // 15 minutes in ms
    const timeout = setTimeout(() => {
      setPolling(0);
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }, maxPollingTime);
    return () => clearTimeout(timeout);
  }, [polling]);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 500);

    return () => {
      clearInterval(timer);
      setProgress(0);
    };
  }, []);

  useEffect(() => {
    if (
      productApplication?.stage &&
      productApplication?.stage !== STAGE?.RPD_MODE
    ) {
      if (productApplication?.stage == STAGE?.E_SIGN) {
        router.push(ROUTES(product).E_SIGN_INSTRUCTIONS);
        return;
      }
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [productApplication?.stage]);

  const isLoading = applicationLoading || reversePennyDropLoading;
  const error = errorReversePennyDrop || errorGetApplication;

  useErrorHandling(error);

  const qrImage = reversePennyDropData?.data?.QRImage
    ? `data:image/jpeg;base64,${reversePennyDropData?.data?.QRImage}`
    : null;

  return (
    <>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <PaperComponent
          paperStyle={{ backgroundColor: "rgba(242, 247, 247, 0.6)" }}
          elevation={0}
        >
          <Stack rowGap={1}>
            <Stack alignItems={"center"} rowGap={1}>
              {reversePennyDropData?.data?.link && (
                <>
                  <Typography fontSize={{ xs: 16, sm: 20 }} fontWeight={500}>
                    <a
                      href={`${reversePennyDropData?.data?.link}`}
                      style={{
                        color: WEB_COLORS.primary,
                        textDecoration: "none",
                      }}
                    >
                      {reversePennyDropData?.data?.link}
                    </a>
                  </Typography>
                  <Typography color={WEB_COLORS.fadeColor}>
                    👆 {t("clickLink")}
                  </Typography>
                  <Typography>{t("or")}</Typography>
                </>
              )}
              {reversePennyDropData?.data?.QRImage && (
                <>
                  <Image
                    src={qrImage}
                    height={150}
                    width={150}
                    alt="qr code"
                    style={{ position: "relative", zIndex: 2 }}
                  />
                  <Typography>{t("scanQR")}</Typography>
                </>
              )}
            </Stack>
            <Stack alignItems={"flex-start"}>
              {[t("afterclicking"), t("onemoney"), t("comeback")].map(
                (text, idx) => (
                  <PaperComponent
                    paperStyle={{ width: "100%" }}
                    elevation={3}
                    key={idx}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={1}
                    >
                      <Box
                        sx={{
                          borderRadius: "50%",
                          background: WEB_COLORS.pale,
                          color: "#000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: 32, sm: 40, md: 44 },
                          height: { xs: 32, sm: 40, md: 44 },
                          minWidth: 32,
                          minHeight: 32,
                        }}
                      >
                        <Typography
                          fontSize={{ xs: 14, sm: 16, md: 18 }}
                          fontWeight={700}
                        >
                          {idx + 1}
                          {"."}
                        </Typography>
                      </Box>
                      <Typography
                        component="span"
                        fontSize={{ xs: 12, sm: 16, md: 20 }}
                      >
                        {text}
                      </Typography>
                    </Stack>
                  </PaperComponent>
                )
              )}
            </Stack>
            <PaperComponent
              paperStyle={{ backgroundColor: "#E5F1F1" }}
              elevation={0}
            >
              <Stack direction={"row"} alignItems={"flex-start"} spacing={1}>
                <Image
                  src={IMAGES.yellowBulb}
                  alt="Notice"
                  height={20}
                  width={20}
                />
                <Stack>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    color={WEB_COLORS.primary}
                  >
                    {t("quickcheck")}
                  </Typography>
                  <Typography variant="body2" color={WEB_COLORS.primary}>
                    {t("ensuretochoose")}
                  </Typography>
                </Stack>
              </Stack>
            </PaperComponent>
            <Box sx={{ minWidth: 300, width: "100%", height: 10 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
          </Stack>
        </PaperComponent>
      )}
    </>
  );
};

export default ReversePennyDrop;
